import React from 'react';
import { Chart } from 'react-google-charts';
import { Table } from 'react-bootstrap';

// Brand colors
const brandColors = {
  primaryDark: '#3b4f46',
  primaryLight: '#567a70',
  secondaryDark: '#d19112',
  secondaryLight: '#ffb41e',
};



export default function UserLocationMap({ cityLocationData = [], regionLocationData = [] }) {
  console.log('🗺️ UserLocationMap DEBUG:', { 
    cityLocationDataCount: cityLocationData.length, 
    regionLocationDataCount: regionLocationData.length,
    cityLocationData: cityLocationData,
    regionLocationData: regionLocationData
  });

  // Filter to show only Dutch cities with data (from real GA)
  const dutchCitiesWithData = cityLocationData
    .filter(city => city.country === 'Netherlands')
    .filter(city => city.activeUsers > 0)
    .sort((a, b) => b.activeUsers - a.activeUsers);

  // Filter to show only Dutch regions with data (from real GA) for map coloring
  const dutchRegionsWithData = regionLocationData
    .filter(region => region.country === 'Netherlands')
    .filter(region => region.activeUsers > 0)
    .sort((a, b) => b.activeUsers - a.activeUsers);

  // Calculate total visitors for Netherlands from regions
  const totalDutchVisitors = dutchRegionsWithData.reduce((sum, region) => sum + region.activeUsers, 0);

  // Map GA region names to Google Charts province codes
  const regionToProvinceCode = {
    'North Holland': 'NL-NH',
    'Noord-Holland': 'NL-NH', 
    'South Holland': 'NL-ZH',
    'Zuid-Holland': 'NL-ZH',
    'Utrecht': 'NL-UT',
    'North Brabant': 'NL-NB',
    'Noord-Brabant': 'NL-NB',
    'Gelderland': 'NL-GE',
    'Overijssel': 'NL-OV',
    'Limburg': 'NL-LI',
    'Friesland': 'NL-FR',
    'Groningen': 'NL-GR',
    'Drenthe': 'NL-DR',
    'Flevoland': 'NL-FL',
    'Zeeland': 'NL-ZE'
  };

  // Reverse mapping from province codes to Dutch names for display
  const provinceCodeToDisplay = {
    'NL-NH': 'Noord-Holland',
    'NL-ZH': 'Zuid-Holland',
    'NL-UT': 'Utrecht',
    'NL-NB': 'Noord-Brabant',
    'NL-GE': 'Gelderland',
    'NL-OV': 'Overijssel',
    'NL-LI': 'Limburg',
    'NL-FR': 'Friesland',
    'NL-GR': 'Groningen',
    'NL-DR': 'Drenthe',
    'NL-FL': 'Flevoland',
    'NL-ZE': 'Zeeland'
  };

  console.log('🗺️ Available region mappings:', Object.keys(regionToProvinceCode));

  // Translate English region names to Dutch
  const regionNameTranslations = {
    'North Holland': 'Noord-Holland',
    'Noord-Holland': 'Noord-Holland',
    'South Holland': 'Zuid-Holland', 
    'Zuid-Holland': 'Zuid-Holland',
    'Utrecht': 'Utrecht',
    'North Brabant': 'Noord-Brabant',
    'Noord-Brabant': 'Noord-Brabant',
    'Gelderland': 'Gelderland',
    'Overijssel': 'Overijssel',
    'Limburg': 'Limburg',
    'Friesland': 'Friesland',
    'Groningen': 'Groningen',
    'Drenthe': 'Drenthe',
    'Flevoland': 'Flevoland',
    'Zeeland': 'Zeeland'
  };

  // Create province data using real GA regions and store region names
  const provinceData = {};
  const provinceToRegionName = {}; // Map province codes back to region names
  console.log('🔍 Processing regions:', dutchRegionsWithData);
  dutchRegionsWithData.forEach(region => {
    const provinceCode = regionToProvinceCode[region.region];
    console.log(`🏛️ Mapping region "${region.region}" to province code "${provinceCode}"`);
    if (provinceCode) {
      provinceData[provinceCode] = region.activeUsers;
      provinceToRegionName[provinceCode] = region.region; // Store the original region name
      console.log(`✅ Added ${region.activeUsers} visitors to province ${provinceCode} (${region.region})`);
    } else {
      console.warn('❌ Unknown region from GA:', region.region);
    }
  });

  // Data for the Netherlands province map using real GA region data with custom labels
  const geoChartData = [
    [
      { label: 'Province', type: 'string' },
      { label: 'Visitors', type: 'number' },
      { role: 'tooltip', type: 'string', p: { html: true } }
    ],
    ...Object.entries(provinceData).map(([provinceCode, visitors]) => {
      const dutchRegionName = provinceCodeToDisplay[provinceCode] || 'Onbekende regio';
      const tooltip = `<div style="padding: 10px; font-family: Arial, sans-serif; font-size: 13px; line-height: 1.4; border: none; background: white; border-radius: 4px;"><div style="font-weight: bold; margin-bottom: 4px; color: #333;">${dutchRegionName}</div><div style="color: #666;">${visitors.toLocaleString('nl-NL')} bezoekers</div></div>`;
      return [provinceCode, visitors, tooltip];
    })
  ];

  console.log('📊 GeoChart data before empty check:', geoChartData);

  // If no real data, show empty map
  if (geoChartData.length === 1) {
    geoChartData.push(['NL-NH', 0]); // Add one empty entry so map still shows
    console.log('⚠️ No province data found, added empty entry');
  }

  console.log('📊 Final GeoChart data:', geoChartData);

  console.log('🗺️ Map Debug:', {
    dutchRegionsCount: dutchRegionsWithData.length,
    totalVisitors: totalDutchVisitors,
    provincesWithData: Object.keys(provinceData).length,
    provinceData: provinceData,
    sampleRegions: dutchRegionsWithData.slice(0, 3),
    regionMapping: dutchRegionsWithData.map(r => ({ 
      gaRegion: r.region, 
      provinceCode: regionToProvinceCode[r.region],
      visitors: r.activeUsers 
    }))
  });

  const geoChartOptions = {
    region: 'NL',
    displayMode: 'regions',
    resolution: 'provinces',
    backgroundColor: '#ffffff',
    colorAxis: Object.keys(provinceData).length > 0 ? {
      colors: [brandColors.primaryLight, brandColors.primaryDark],
      minValue: 0
    } : {
      colors: ['#e8f4f8', '#e8f4f8'],
      minValue: 0,
      maxValue: 1
    },
    tooltip: {
      isHtml: true
    },
    legend: 'none',
    width: '100%',
    keepAspectRatio: true,
    enableRegionInteractivity: true
  };



  return (
    <div style={{ width: '100%' }}>




      {/* Google GeoChart - Netherlands Map */}
      <div style={{ 
        width: '100%', 
        aspectRatio: '3 / 2',
        position: 'relative', 
        marginBottom: '20px',
        borderRadius: '8px',
        backgroundColor: '#f0f8ff',
        overflow: 'hidden'
      }}>
        <Chart
          chartType="GeoChart"
          data={geoChartData}
          options={geoChartOptions}
          width="100%"
          height="100%"
        />
      </div>

      {/* City Data Table */}
      <div style={{ marginTop: '20px' }}>
        {dutchCitiesWithData.length > 0 ? (
          <Table hover>
            <thead>
              <tr>
                <th>Stad</th>
                <th className="text-end">Bezoekers</th>
              </tr>
            </thead>
            <tbody>
              {dutchCitiesWithData.map((city, index) => (
                <tr key={index}>
                  <td>
                    <div className="d-flex align-items-center">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="me-2">
                        <path d="M12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5M12,2A7,7 0 0,0 5,9C5,14.25 12,22 12,22C12,22 19,14.25 19,9A7,7 0 0,0 12,2Z" fill="currentColor"/>
                      </svg>
                                             <div>
                         <div>{city.city}</div>
                         <small className="text-muted" style={{fontSize: '0.75rem', lineHeight: 1.2}}>
                           {city.region ? (regionNameTranslations[city.region] || city.region) : city.country}
                         </small>
                       </div>
                    </div>
                  </td>
                  <td className="text-end" style={{color: brandColors.primaryLight}}>
                    {city.activeUsers.toLocaleString('nl-NL')}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <div className="text-center py-4">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" className="mb-3" style={{opacity: 0.3}}>
              <path d="M12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5M12,2A7,7 0 0,0 5,9C5,14.25 12,22 12,22C12,22 19,14.25 19,9A7,7 0 0,0 12,2Z" fill="currentColor"/>
            </svg>
            <h6 className="text-muted">Geen stadsgegevens beschikbaar</h6>
            <p className="text-muted small mb-0">Geen GA4 data beschikbaar voor de geselecteerde periode</p>
          </div>
        )}
      </div>
    </div>
  );
} 